import { Events } from '@platform/client';
import { DatePickerWithTimezone, Dropdown, DropdownItem, Input, Modal } from '@platform/components';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

interface CreateEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  resetPagination: () => void;
  limit: number;
}

export const CreateEventModal = (props: CreateEventModalProps) => {
  const { isOpen, onClose, resetPagination, limit } = props;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [hostedBy, setHostedBy] = useState('');

  const [startTime, setStartTime] = useState(new Date());
  const [endTime, setEndTime] = useState(new Date(Date.now() + 60 * 60 * 1000));

  const [timezone, setTimezone] = useState('America/Los_Angeles');
  const [link, setLink] = useState('');

  const queryClient = useQueryClient();

  const submitEvent = Events.useCreateEvent({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [...Events.getGetEventsAdminQueryKey({ page: 0, limit })],
        });
        resetPagination();
      },
    },
  });

  const createEvent = async () => {
    submitEvent.mutate({
      data: {
        title,
        description,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        hostedBy,
        timezone,
        link,
      },
    });

    closeModal();
  };

  const updateTimezone = (tz: string) => {
    setTimezone(tz);
  };

  const closeModal = () => {
    onClose();
    setTitle('');
    setDescription('');
    setStartTime(new Date());
    setEndTime(new Date());
    setHostedBy('');
    setLink('');
    setTimezone('America/Los_Angeles');
  };

  return (
    <Modal
      variant='dialog'
      title='Create Event'
      isOpen={isOpen}
      headerDivision='divider'
      onClose={closeModal}
      ignoreExtraPadding={true}
      primaryCTA={{
        text: 'Create Event',
        callback: createEvent,
      }}
      secondaryCTA={{
        text: 'Cancel',
        callback: closeModal,
      }}
    >
      <div className='md:w-[460px]'>
        <div className='flex flex-col gap-4 w-full'>
          <Input
            label='Title'
            type='inline'
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Input
            label='Hosted By'
            type='inline'
            value={hostedBy}
            onChange={(e) => setHostedBy(e.target.value)}
          />
          <Input
            label='Description'
            type='multiline'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Input
            label='Link'
            type='inline'
            value={link}
            onChange={(e) => setLink(e.target.value)}
          />
          <Dropdown
            type='button'
            ariaLabel='Timezone Dropdown'
            buttonProps={{
              variant: 'secondary-gray',
              size: 'lg',
              children: <div className='p-4'>{timezone ? timezone : 'Select a Timezone'}</div>,
            }}
          >
            {Intl.supportedValuesOf('timeZone').map((tz) => {
              return (
                <DropdownItem key={tz} onAction={() => updateTimezone(tz)}>
                  {tz}
                </DropdownItem>
              );
            })}
          </Dropdown>
          <DatePickerWithTimezone
            includeTime={true}
            label={'Start Time'}
            date={startTime}
            timezone={timezone}
            setDate={(newDate) => setStartTime(newDate)}
          />
          <DatePickerWithTimezone
            includeTime={true}
            label='End Time'
            date={endTime}
            timezone={timezone}
            setDate={(newDate) => setEndTime(newDate)}
          />
        </div>
      </div>
    </Modal>
  );
};
